import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useAuth } from '@/features/auth';
import { useLocale } from 'use-intl';
import { Header, HeaderBackButton, HeaderCopyButton, HeaderUserMenu } from '@/ui/components/header';
import { Breadcrumb, BreadcrumbLink, BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Icon } from '@/ui/components/icon';
import { SparklesIcon } from '@hugeicons-pro/core-stroke-rounded';
import {
  MainContainer,
  MainHeader,
  MainHeaderBackButton,
  MainScrollArea,
} from '@/ui/components/main';
import { H1, H2 } from '@/ui/components/heading';
import { LearningsActivityOverview } from '@/features/learnings/components/learnings-activity-overview';
import { toProjectType } from '@/features/projects/utils';
import { ProjectTypeIcon } from '@/features/projects/components/project-type-icon';
import { learningsQuery } from '@/features/learnings/queries';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from '@/ui/components/spinner';
import { LearningsStatements } from '@/features/learnings/components/learnings-statements';
import { dateFormat, formatDate } from '@/dates';
import {
  AISummaryAlert,
  AISummaryAlertCopyButton,
  AISummaryAlertDescription,
  AISummaryAlertTitle,
} from '@/ui/components/ai-summary-alert';

export const Route = createFileRoute('/_authed/_organization/learnings/portfolio/$projectType')({
  component: RouteComponent,
  params: {
    parse: (params) => {
      const projectType = toProjectType(params.projectType);
      if (!projectType) throw notFound();
      return { projectType };
    },
  },
});

function RouteComponent() {
  const { projectType } = Route.useParams();
  const locale = useLocale();
  const auth = useAuth();
  const organizationId = auth.user.organizationId;
  const { data, isLoading } = useQuery(learningsQuery(organizationId, projectType));

  const learning = data?.insights?.[0];
  if (isLoading) return <Spinner kind="inset" />;
  if (!learning) return null;

  return (
    <>
      <Header>
        <HeaderBackButton to="/learnings" />
        <Breadcrumb>
          <BreadcrumbLink to={'/learnings'}>
            <Icon icon={SparklesIcon} />
            {t({ en: 'Learnings', nb: 'Erfaringer' })}
          </BreadcrumbLink>
          <BreadcrumbPage>
            <ProjectTypeIcon type={projectType} />
            <span className="line-clamp-1">
              {t(common.projectType[projectType])}{' '}
              <span className="lowercase">{t({ en: 'Learnings', nb: 'Erfaringer' })}</span>
            </span>
          </BreadcrumbPage>
        </Breadcrumb>
        <HeaderUserMenu />
        <HeaderCopyButton />
      </Header>
      <MainScrollArea>
        <MainHeader>
          <H1>
            <MainHeaderBackButton to="/learnings" />
            {t(common.projectType[projectType])}{' '}
            <span className="lowercase">{t({ en: 'Learnings', nb: 'Erfaringer' })}</span>
          </H1>
        </MainHeader>
        <MainContainer className="pb-0">
          <LearningsActivityOverview projectType={projectType} />
          <section>
            <H2 className="mb-3 mt-5">
              {t({ en: 'Summary', nb: 'Sammendrag' })}{' '}
              {learning.lastUpdatedAt ? (
                <span className="text-muted font-normal ml-1 text-sm">
                  {t({
                    en: `Last updated: ${formatDate(learning.lastUpdatedAt, dateFormat.dayFirst, locale)}`,
                    nb: `Sist oppdatert: ${formatDate(learning.lastUpdatedAt, dateFormat.dayFirst, locale)}`,
                  })}
                </span>
              ) : null}
            </H2>
            <AISummaryAlert text={learning.summary}>
              <AISummaryAlertTitle>
                <AISummaryAlertCopyButton />
              </AISummaryAlertTitle>
              <AISummaryAlertDescription />
            </AISummaryAlert>
          </section>
        </MainContainer>
        <LearningsStatements data={data} />
      </MainScrollArea>
    </>
  );
}
